/**
 * API Service for making authenticated requests to the backend
 */
import { resolvedAuthUser } from '../api/resolvedAuthUser';
import { backendApiUrl } from '../config/backendApiUrl';
import {
  applyDealerTenantToPayload,
  applyDealerTenantToUrl,
  dealerAdminHeaders,
} from '../pages/Admin/dealerAdminSession';
import {
  applySupportTenantToUrl,
  supportHeadersForEndpoint,
} from '../pages/Admin/supportedTenantsSession';

// Debug logging gate for generic API calls (enable via: window.__DEBUG_API__ = true)
const API_DEBUG = typeof window !== 'undefined' && window.__DEBUG_API__ === true;

/**
 * Make an authenticated API request with the current user's ID token
 *
 * @param {string} url - The API endpoint URL
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {object} data - Request body for POST/PUT requests
 * @param {object} customHeaders - Additional headers to include
 * @returns {Promise<object>} Response data
 */
// Resolve API base URL for relative paths when Hosting rewrites are not available
const resolveApiUrl = (url) => {
  try {
    if (typeof url === 'string' && (url.startsWith('/api/') || url === '/api' || /^https?:\/\//i.test(url))) {
      return backendApiUrl(url);
    }
    const isAbsolute = /^https?:\/\//i.test(url);
    if (isAbsolute) return url;

    // Prefer explicit runtime base URL if provided
    const winBase = (typeof window !== 'undefined' && window.__API_BASE_URL__)
      ? String(window.__API_BASE_URL__)
      : '';

    // Fall back to Vite env at build-time if present
    let envBase = '';
    try {
      // Access import.meta in a guarded manner; this is valid in Vite builds
      // eslint-disable-next-line no-undef
      envBase = (typeof import.meta !== 'undefined' && import.meta.env.VITE_API_URL)
        ? String(import.meta.env.VITE_API_URL)
        : '';
    } catch (_ignored) {
      envBase = '';
    }

    const base = (winBase || envBase || '').replace(/\/+$/, '');
    if (!base) return url; // fall back to relative (expecting Hosting rewrites)
    return `${base}${url.startsWith('/') ? url : `/${url}`}`;
  } catch (_e) {
    return url;
  }
};

export const fetchWithAuth = async (url, method = 'GET', data = null, customHeaders = {}) => {
  try {
    // Get the current user
    const user = await resolvedAuthUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get the current user's ID token
    const token = await user.getIdToken();

    // Prepare request headers
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...customHeaders,
      ...dealerAdminHeaders(),
      ...supportHeadersForEndpoint(url),
    };

    // Prepare request options
    const options = {
      method,
      headers,
      credentials: 'include',
    };

    // Add request body for non-GET requests
    if (data && method !== 'GET') {
      options.body = JSON.stringify(applyDealerTenantToPayload(data));
    }

    // Resolve URL (supports Hosting rewrites or explicit base URL)
    const requestUrl = resolveApiUrl(applySupportTenantToUrl(applyDealerTenantToUrl(url)));

    // Make the request
    if (API_DEBUG) console.log(`[API] ${method} ${requestUrl}`);
    const response = await fetch(requestUrl, options);

    // Parse response
    let responseData;
    const contentType = response.headers.get('content-type');

    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    // Check if request was successful
    if (!response.ok) {
      // Format error response with more detailed information
      const errorMessage =
        // Prefer explicit server error fields when JSON
        (responseData && typeof responseData === 'object' && (responseData.error || responseData.message)) ||
        // If server returned text, surface it
        (typeof responseData === 'string' && responseData) ||
        // Fallback to generic
        (response.status === 400
          ? `Bad Request: Invalid parameters for ${url}`
          : 'API request failed');

      const error = new Error(errorMessage);
      error.status = response.status;
      error.response = responseData;
      error.url = requestUrl;
      error.method = method;

      // Log detailed error information for debugging, but be less verbose for common 404s
      if (
        response.status === 404 &&
        (url.includes('/api/devices') || url.includes('/api/actions'))
      ) {
        console.warn(
          `API endpoint not found (${response.status}): ${url} - This may be expected during development`
        );
      } else {
        if (API_DEBUG) {
          console.error(`API Error (${response.status}):`, {
            url,
            method,
            status: response.status,
            statusText: response.statusText,
            responseData,
          });
        } else {
          console.error(`[API][${method}] ${response.status} ${url}`);
        }
      }

      throw error;
    }

    return responseData;
  } catch (error) {
    if (API_DEBUG) {
      console.error('API request failed:', error);
    } else {
      console.error(`[API][${error.method || 'GET'}] net ${error.url || ''} ${error.message}`.trim());
    }
    throw error;
  }
};

/**
 * Helper function for making GET requests
 */
export const get = (url, customHeaders = {}) => {
  return fetchWithAuth(url, 'GET', null, customHeaders);
};

/**
 * Helper function for making POST requests
 */
export const post = (url, data, customHeaders = {}) => {
  return fetchWithAuth(url, 'POST', data, customHeaders);
};

/**
 * Helper function for making PUT requests
 */
export const put = (url, data, customHeaders = {}) => {
  return fetchWithAuth(url, 'PUT', data, customHeaders);
};

/**
 * Helper function for making DELETE requests
 */
export const del = (url, customHeaders = {}) => {
  return fetchWithAuth(url, 'DELETE', null, customHeaders);
};
